// src/components/RequestSalaryAdjustment.js
import React, { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  useMediaQuery,
  LinearProgress,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  Snackbar,
  Alert,
  Fade,
  Grow,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Popper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CardGiftcard as BonusIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  School as SchoolIcon,
} from '@mui/icons-material';

import Sidebar from '../../../../components/sidebars/Sidebar_HR';
import TopNav from '../../../../components/TopNav_hotelStaff';

const RequestSalaryAdjustment = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [requestType, setRequestType] = useState('initial');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [staffInfo, setStaffInfo] = useState(null);
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAdjustments, setPendingAdjustments] = useState([]);
  const [pendingBonuses, setPendingBonuses] = useState([]);
  const [searchError, setSearchError] = useState('');
  
  const searchDebounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Get API base URL from environment or use default
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost/Hotel/api';

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  // Format currency for display
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'Not set';
    const roundedAmount = Math.round(amount * 100) / 100;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(roundedAmount);
  };

  // Search employees using get_employee_info.php search endpoint
  const searchEmployees = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();

    setSearchLoading(true);
    setSearchError('');
    
    try {
      // Use the search action on get_employee_info.php
      const response = await axios.get(
        `${API_BASE_URL}/hotel/HR/get_employee_info.php?action=search&search=${encodeURIComponent(searchTerm)}`,
        { signal: abortControllerRef.current.signal }
      );
      
      if (response.data.success && response.data.data) {
        const employees = response.data.data;
        
        // Format employees for display
        const formattedEmployees = employees.map(emp => ({
          id: emp.employee_id,
          employee_id: emp.employee_id,
          surname: emp.surname,
          other_names: emp.other_names,
          full_name: emp.full_name,
          department: emp.department,
          phone: emp.phone,
          email: emp.email,
          salary_amount: parseFloat(emp.salary_amount) || 0,
          monthly_salary: parseFloat(emp.salary_amount) || 0,
          status: emp.employee_status || 'Active',
          is_active: parseInt(emp.is_active) || 1,
          user_type: emp.user_type,
          staff_code: emp.staff_code,
          gender: emp.gender
        }));
        
        setSearchResults(formattedEmployees);
        
        if (formattedEmployees.length === 0) {
          setSearchError('No employees found matching your search');
        } else {
          setSearchError('');
        }
      } else {
        setSearchResults([]);
        setSearchError(response.data.message || 'No employees found');
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
        console.error('Error searching employees:', error);
        setSearchResults([]);
        setSearchError('Error searching for employees. Please try again.');
      }
    } finally {
      setSearchLoading(false);
      abortControllerRef.current = null;
    }
  }, [API_BASE_URL]);

  // Debounced search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Don't search if the input matches the selected employee's display format
    if (selectedEmployee && 
        `${selectedEmployee.surname} ${selectedEmployee.other_names} (${selectedEmployee.phone})` === searchInput) {
      return;
    }
    
    if (searchInput.trim().length >= 2) {
      searchDebounceRef.current = setTimeout(() => {
        searchEmployees(searchInput);
      }, 400);
    } else {
      setSearchResults([]);
      setSearchError('');
    }
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput, selectedEmployee, searchEmployees]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Fetch complete staff info using phone number
  const fetchStaffInfoByPhone = useCallback(async (phone) => {
    if (!phone) {
      setStaffInfo(null);
      setPendingAdjustments([]);
      setPendingBonuses([]);
      return;
    }

    setFetchingInfo(true);
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/hotel/HR/get_employee_info.php?phone=${encodeURIComponent(phone)}`
      );
      
      if (response.data.success && response.data.data) {
        const employeeData = response.data.data;
        setStaffInfo(employeeData.employee);
        setPendingAdjustments(employeeData.pending_adjustments || []);
        setPendingBonuses(employeeData.pending_bonuses || []);
      } else {
        setStaffInfo(null);
        setPendingAdjustments([]);
        setPendingBonuses([]);
        setSnackbar({ open: true, message: response.data.message || 'Employee not found', severity: 'error' });
      }
    } catch (error) {
      console.error('Error fetching staff info:', error);
      setStaffInfo(null);
      setPendingAdjustments([]);
      setPendingBonuses([]);
      if (error.response?.status === 404) {
        setSnackbar({ open: true, message: 'Employee not found or inactive', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: 'Error fetching employee information', severity: 'error' });
      }
    } finally {
      setFetchingInfo(false);
    }
  }, [API_BASE_URL]);

  // Handle employee selection from dropdown
  const handleEmployeeSelect = (event, employee) => {
    if (employee) {
      setSelectedEmployee(employee);
      setSearchInput(`${employee.surname} ${employee.other_names} (${employee.phone})`);
      setSearchResults([]);
      setSearchError('');
      // Fetch full details using phone number
      fetchStaffInfoByPhone(employee.phone);
      setRequestSubmitted(false);
      setAmount('');
      setReason('');
    } else {
      handleClearSelection();
    }
  };

  const handleClearSelection = () => {
    setSelectedEmployee(null);
    setSearchInput('');
    setStaffInfo(null);
    setPendingAdjustments([]);
    setPendingBonuses([]);
    setAmount('');
    setReason('');
    setSearchResults([]);
    setSearchError('');
  };

  const handleRequestTypeChange = (event, newType) => {
    if (newType !== null) {
      setRequestType(newType);
      setAmount('');
      setReason('');
    }
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    
    // Remove any non-numeric characters except decimal point
    value = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    setAmount(value);
  };

  const validateRequest = () => {
    if (!selectedEmployee) {
      setSnackbar({ open: true, message: '❌ Please select an employee from the search results', severity: 'error' });
      return false;
    }

    if (!staffInfo) {
      setSnackbar({ open: true, message: '❌ Please wait for employee information to load', severity: 'error' });
      return false;
    }

    let parsedAmount = parseFloat(amount);
    
    if (isNaN(parsedAmount)) {
      setSnackbar({ open: true, message: '❌ Please enter a valid amount', severity: 'error' });
      return false;
    }
    
    parsedAmount = Math.round(parsedAmount * 100) / 100;
    
    if (requestType === 'initial') {
      if (parsedAmount <= 0) {
        setSnackbar({ open: true, message: '❌ Initial salary must be greater than 0', severity: 'error' });
        return false;
      }
      
      if (staffInfo?.salary_amount > 0) {
        setSnackbar({ open: true, message: '❌ Employee already has a salary. Use Increment instead.', severity: 'error' });
        return false;
      }
    } else if (requestType === 'bonus') {
      if (parsedAmount < 0) {
        setSnackbar({ open: true, message: '❌ Bonus amount cannot be negative', severity: 'error' });
        return false;
      }
    } else {
      if (parsedAmount <= 0) {
        setSnackbar({ open: true, message: '❌ Please enter a valid positive amount', severity: 'error' });
        return false;
      }
    }

    if (requestType === 'downgrade' && staffInfo && parsedAmount > staffInfo.salary_amount) {
      setSnackbar({ 
        open: true, 
        message: `❌ Downgrade amount (₦${parsedAmount.toLocaleString()}) cannot exceed current salary (₦${staffInfo.salary_amount.toLocaleString()})`, 
        severity: 'error' 
      });
      return false;
    }

    if (!reason.trim() || reason.trim().length < 5) {
      setSnackbar({ open: true, message: '❌ Please provide a detailed reason (minimum 5 characters)', severity: 'error' });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateRequest()) {
      return;
    }
    setConfirmDialogOpen(true);
  };

  const confirmSubmit = async () => {
    setConfirmDialogOpen(false);
    setLoading(true);

    try {
      let parsedAmount = parseFloat(amount);
      parsedAmount = Math.round(parsedAmount * 100) / 100;

      const payload = {
        phone: selectedEmployee.phone,
        request_type: requestType,
        amount: parsedAmount,
        reason: reason.trim(),
        requested_by: localStorage.getItem('userName') || 'HR'
      };

      console.log('Submitting payload:', payload);

      const response = await axios.post(
        `${API_BASE_URL}/hotel/HR/update_salary.php`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );

      console.log('Response:', response.data);

      if (response.data.success) {
        let successMessage = '';
        if (requestType === 'initial') {
          successMessage = `✅ Initial salary of ${formatCurrency(parsedAmount)} set successfully for ${staffInfo?.full_name}!`;
        } else if (requestType === 'increment') {
          successMessage = `✅ Increment request of ${formatCurrency(parsedAmount)} submitted for approval!`;
        } else if (requestType === 'downgrade') {
          successMessage = `✅ Downgrade request of ${formatCurrency(parsedAmount)} submitted for approval!`;
        } else {
          successMessage = `✅ Bonus request of ${formatCurrency(parsedAmount)} submitted for approval!`;
        }
        
        setSnackbar({ open: true, message: successMessage, severity: 'success' });
        setRequestSubmitted(true);
        setAmount('');
        setReason('');
        
        // Refresh staff info after 2 seconds
        setTimeout(() => {
          if (selectedEmployee) {
            fetchStaffInfoByPhone(selectedEmployee.phone);
          }
        }, 2000);
        
        // Reset form after 3 seconds if initial salary was set
        if (requestType === 'initial') {
          setTimeout(() => {
            handleClearSelection();
            setRequestSubmitted(false);
            setRequestType('initial');
          }, 3000);
        }
      } else {
        setSnackbar({ open: true, message: response.data.message || '⚠️ Request failed.', severity: 'error' });
      }
    } catch (error) {
      console.error('Request error:', error);
      const errorMessage = error.response?.data?.message || error.message || '❌ Error submitting request';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getPreviewAmounts = () => {
    const current = staffInfo?.salary_amount || 0;
    const parsedAmount = parseFloat(amount) || 0;
    const roundedCurrent = Math.round(current * 100) / 100;
    const roundedAmount = Math.round(parsedAmount * 100) / 100;
    
    let newAmount = roundedCurrent;
    let changeText = '';
    
    if (requestType === 'initial') {
      newAmount = roundedAmount;
      changeText = '=';
    } else if (requestType === 'increment') {
      newAmount = roundedCurrent + roundedAmount;
      changeText = '+';
    } else if (requestType === 'downgrade') {
      newAmount = roundedCurrent - roundedAmount;
      changeText = '-';
    } else {
      newAmount = roundedCurrent;
      changeText = '+ (Bonus)';
    }
    
    const roundedNew = Math.round(newAmount * 100) / 100;
    
    return {
      current: roundedCurrent,
      amount: roundedAmount,
      newValue: roundedNew,
      changeText: changeText
    };
  };

  const preview = getPreviewAmounts();

  const getRequestTypeConfig = () => {
    switch (requestType) {
      case 'initial':
        return {
          icon: <SchoolIcon />,
          color: '#2196f3',
          bgColor: '#e3f2fd',
          title: 'Initial Salary Setup',
          description: 'Set initial salary for new employee (overwrites 0.00)',
          buttonText: 'Set Initial Salary',
          amountLabel: 'Initial Salary Amount (₦)',
          reasonLabel: 'Justification / Remarks',
          previewLabel: 'New Salary Amount'
        };
      case 'increment':
        return {
          icon: <TrendingUpIcon />,
          color: '#4caf50',
          bgColor: '#e8f5e9',
          title: 'Salary Increment',
          description: 'Request a salary increase for this employee',
          buttonText: 'Submit Increment Request',
          amountLabel: 'Increment Amount (₦)',
          reasonLabel: 'Reason for Increment',
          previewLabel: 'New Salary After Increment'
        };
      case 'downgrade':
        return {
          icon: <TrendingDownIcon />,
          color: '#f44336',
          bgColor: '#ffebee',
          title: 'Salary Downgrade',
          description: 'Request a salary reduction for this employee',
          buttonText: 'Submit Downgrade Request',
          amountLabel: 'Downgrade Amount (₦)',
          reasonLabel: 'Reason for Downgrade',
          previewLabel: 'New Salary After Downgrade'
        };
      default:
        return {
          icon: <BonusIcon />,
          color: '#ff9800',
          bgColor: '#fff3e0',
          title: 'Bonus Request',
          description: 'Request a one-time bonus for this employee',
          buttonText: 'Submit Bonus Request',
          amountLabel: 'Bonus Amount (₦)',
          reasonLabel: 'Reason for Bonus',
          previewLabel: 'Bonus Amount (One-time)'
        };
    }
  };

  const config = getRequestTypeConfig();

  // Check if inputs should be enabled
  const inputsEnabled = selectedEmployee !== null && staffInfo !== null && !fetchingInfo;

  // Custom popper for autocomplete
  const CustomPopper = (props) => {
    return (
      <Popper {...props} placement="bottom-start" style={{ width: props.style?.width, zIndex: 1300 }}>
        <Paper elevation={4} sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }}>
          {searchLoading && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <CircularProgress size={30} />
              <Typography variant="body2" sx={{ mt: 1 }}>Searching...</Typography>
            </Box>
          )}
          {!searchLoading && searchError && !searchResults.length && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="error">{searchError}</Typography>
            </Box>
          )}
          {!searchLoading && !searchError && searchResults.length === 0 && searchInput.length >= 2 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">No employees found</Typography>
            </Box>
          )}
          {props.children}
        </Paper>
      </Popper>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box sx={{ flexGrow: 1, p: 3, ml: isMobile ? 0 : sidebarOpen ? 30 : 5, maxWidth: '1400px', mx: 'auto', width: '100%', overflow: 'auto', height: '100vh' }}>
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
            localStorage.removeItem('userPhone');
            localStorage.removeItem('userName');
            window.location.href = '/';
          }}
          onNotificationClick={() => {
            setSnackbar({ open: true, message: 'You have new notifications!', severity: 'info' });
          }}
          toggleSidebar={toggleSidebar}
        />

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={7}>
            <Fade in timeout={500}>
              <Paper elevation={4} sx={{ p: 4, borderRadius: 4, background: '#ffffff', boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ bgcolor: '#C84646', width: 56, height: 56 }}>
                    <MoneyIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold" color="#C84646">Salary Management</Typography>
                    <Typography variant="body2" color="textSecondary">Set initial salary, submit increment, downgrade, or bonus requests</Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Search Employee (by Name or Phone)</Typography>
                
                {/* Employee Search with Autocomplete */}
                <Autocomplete
                  freeSolo
                  options={searchResults}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return `${option.surname} ${option.other_names} (${option.phone})`;
                  }}
                  value={selectedEmployee}
                  onChange={handleEmployeeSelect}
                  inputValue={searchInput}
                  onInputChange={(event, newInputValue, reason) => {
                    // Only update search input and search when it's a user typing (not from selection)
                    if (reason === 'input') {
                      setSearchInput(newInputValue);
                      if (!newInputValue) {
                        handleClearSelection();
                      }
                    } else if (reason === 'reset') {
                      // Don't trigger search on reset
                      return;
                    }
                  }}
                  loading={searchLoading}
                  disabled={loading}
                  PopperComponent={CustomPopper}
                  filterOptions={(x) => x} // Disable default filtering (we do our own)
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Type employee name or phone number (min. 2 characters)..."
                      variant="outlined"
                      error={!!searchError}
                      helperText={searchError || "Search by name (e.g., John, Nicholas) or phone number"}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: '#C84646' }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {searchLoading && <CircularProgress size={20} />}
                            {selectedEmployee && (
                              <IconButton size="small" onClick={handleClearSelection} sx={{ mr: 1 }}>
                                <ClearIcon />
                              </IconButton>
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const hasSalary = option.salary_amount > 0 || option.monthly_salary > 0;
                    const salaryAmount = option.salary_amount || option.monthly_salary || 0;
                    return (
                      <li {...props}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: hasSalary ? '#4caf50' : '#ff9800', width: 40, height: 40 }}>
                              <PersonIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="body1" fontWeight="bold">
                                {option.surname} {option.other_names}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip 
                                  size="small" 
                                  icon={<BusinessIcon sx={{ fontSize: 14 }} />} 
                                  label={option.department || 'No Dept'} 
                                  variant="outlined" 
                                />
                                <Chip 
                                  size="small" 
                                  icon={<PhoneIcon sx={{ fontSize: 14 }} />} 
                                  label={option.phone} 
                                  variant="outlined" 
                                />
                                <Chip 
                                  size="small" 
                                  label={hasSalary ? formatCurrency(salaryAmount) : 'No Salary'} 
                                  sx={{ 
                                    bgcolor: hasSalary ? '#e8f5e9' : '#fff3e0',
                                    color: hasSalary ? '#2e7d32' : '#ed6c02'
                                  }}
                                />
                              </Box>
                            </Box>
                          </Box>
                          <Chip 
                            size="small" 
                            label={option.status === 'Active' ? 'Active' : 'Inactive'} 
                            color={option.status === 'Active' ? 'success' : 'default'}
                          />
                        </Box>
                      </li>
                    );
                  }}
                />

                {selectedEmployee && (
                  <>
                    {fetchingInfo && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <LinearProgress />
                        <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                          Loading employee details...
                        </Typography>
                      </Box>
                    )}

                    {staffInfo && !fetchingInfo && (
                      <Grow in={!!staffInfo}>
                        <Card sx={{ mb: 3, borderRadius: 3, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                              <Typography variant="subtitle2" color="textSecondary" fontWeight="bold">Staff Information</Typography>
                              <Chip 
                                label={staffInfo.salary_amount > 0 ? `Current: ${formatCurrency(staffInfo.salary_amount)}` : 'No Salary Set'} 
                                size="small" 
                                color={staffInfo.salary_amount > 0 ? 'success' : 'warning'} 
                              />
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PersonIcon sx={{ fontSize: 20, color: '#C84646' }} />
                                  <Typography variant="body2"><strong>Name:</strong> {staffInfo.full_name || 'N/A'}</Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <BusinessIcon sx={{ fontSize: 20, color: '#C84646' }} />
                                  <Typography variant="body2"><strong>Department:</strong> {staffInfo.department || 'N/A'}</Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PhoneIcon sx={{ fontSize: 20, color: '#C84646' }} />
                                  <Typography variant="body2"><strong>Phone:</strong> {staffInfo.phone || 'N/A'}</Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <EmailIcon sx={{ fontSize: 20, color: '#C84646' }} />
                                  <Typography variant="body2"><strong>Email:</strong> {staffInfo.email || 'N/A'}</Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            
                            {/* Pending Adjustments Accordion */}
                            {pendingAdjustments.length > 0 && (
                              <Accordion sx={{ mt: 2, bgcolor: '#fff3e0' }} elevation={0}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography variant="caption" color="warning.main" fontWeight="bold">
                                    ⚠️ Pending Adjustments ({pendingAdjustments.length})
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  {pendingAdjustments.map(adj => (
                                    <Box key={adj.id} sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                      <Typography variant="caption" display="block">
                                        <strong>{adj.adjustment_type === 'increment' ? '📈 Increment' : '📉 Downgrade'}</strong>
                                        : {formatCurrency(adj.amount)} | 
                                        Status: <Chip label={adj.approval_status} size="small" color="warning" />
                                      </Typography>
                                      <Typography variant="caption" display="block" color="textSecondary">
                                        {adj.reason}
                                      </Typography>
                                    </Box>
                                  ))}
                                </AccordionDetails>
                              </Accordion>
                            )}
                            
                            {/* Pending Bonuses Accordion */}
                            {pendingBonuses.length > 0 && (
                              <Accordion sx={{ mt: 2, bgcolor: '#e8f5e9' }} elevation={0}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography variant="caption" color="success.main" fontWeight="bold">
                                    💰 Pending Bonuses ({pendingBonuses.length})
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  {pendingBonuses.map(bonus => (
                                    <Box key={bonus.id} sx={{ mb: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                      <Typography variant="caption" display="block">
                                        <strong>💰 Bonus</strong>: {formatCurrency(bonus.bonus_amount)} | 
                                        Status: <Chip label={bonus.approval_status} size="small" color="warning" />
                                      </Typography>
                                      <Typography variant="caption" display="block" color="textSecondary">
                                        {bonus.reason}
                                      </Typography>
                                    </Box>
                                  ))}
                                </AccordionDetails>
                              </Accordion>
                            )}
                          </CardContent>
                        </Card>
                      </Grow>
                    )}
                  </>
                )}

                {/* Request Type - Only enabled when staff is found */}
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Request Type</Typography>
                <ToggleButtonGroup
                  value={requestType}
                  exclusive
                  onChange={handleRequestTypeChange}
                  aria-label="request type"
                  sx={{ mb: 3, width: '100%', flexWrap: 'wrap', gap: 1 }}
                  disabled={!inputsEnabled}
                >
                  <ToggleButton value="initial" aria-label="initial" sx={{ flex: 1, py: 1.5 }}>
                    <SchoolIcon sx={{ mr: 1 }} /> Initial Setup
                  </ToggleButton>
                  <ToggleButton value="increment" aria-label="increment" sx={{ flex: 1, py: 1.5 }}>
                    <TrendingUpIcon sx={{ mr: 1 }} /> Increment
                  </ToggleButton>
                  <ToggleButton value="downgrade" aria-label="downgrade" sx={{ flex: 1, py: 1.5 }}>
                    <TrendingDownIcon sx={{ mr: 1 }} /> Downgrade
                  </ToggleButton>
                  <ToggleButton value="bonus" aria-label="bonus" sx={{ flex: 1, py: 1.5 }}>
                    <BonusIcon sx={{ mr: 1 }} /> Bonus
                  </ToggleButton>
                </ToggleButtonGroup>

                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: config.color }}>
                  {config.amountLabel}
                </Typography>
                <TextField
                  fullWidth
                  type="text"
                  placeholder="Enter amount (e.g., 50000 or 50000.50)"
                  value={amount}
                  onChange={handleAmountChange}
                  disabled={loading || !inputsEnabled}
                  sx={{ mb: 1 }}
                  InputProps={{ 
                    startAdornment: (<InputAdornment position="start"><MoneyIcon sx={{ color: config.color }} /></InputAdornment>),
                  }}
                  helperText={
                    requestType === 'initial' ? "Set the employee's starting salary" :
                    requestType === 'downgrade' ? "Amount to reduce from current salary" : 
                    "Enter amount without commas (e.g., 50000 for ₦50,000)"
                  }
                />

                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, mt: 2, color: config.color }}>
                  {config.reasonLabel}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={requestType === 'initial' ? "e.g., New hire - Marketing Manager" : "Provide a detailed reason for this request..."}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={loading || !inputsEnabled}
                  sx={{ mb: 3 }}
                />

                {staffInfo && amount && parseFloat(amount) > 0 && inputsEnabled && (
                  <Alert severity={requestType === 'initial' ? 'info' : (requestType === 'increment' ? 'success' : (requestType === 'downgrade' ? 'warning' : 'info'))} sx={{ mb: 3, borderRadius: 2 }}>
                    <strong>Preview:</strong><br />
                    {requestType !== 'initial' && `Current Salary: ${formatCurrency(preview.current)}<br />`}
                    {requestType === 'initial' && `Initial Salary to Set: ${formatCurrency(preview.amount)}<br />`}
                    {requestType === 'increment' && `+ Increment: ${formatCurrency(preview.amount)}<br />`}
                    {requestType === 'downgrade' && `- Downgrade: ${formatCurrency(preview.amount)}<br />`}
                    {requestType === 'bonus' && `Bonus Amount: ${formatCurrency(preview.amount)}<br />`}
                    {requestType !== 'bonus' && requestType !== 'initial' && (
                      <>
                        <strong style={{ color: requestType === 'increment' ? '#4caf50' : '#f44336' }}>
                          {config.previewLabel}: {formatCurrency(preview.newValue)}
                        </strong>
                      </>
                    )}
                    {requestType === 'initial' && (
                      <strong style={{ color: '#2196f3' }}>
                        {config.previewLabel}: {formatCurrency(preview.newValue)}
                      </strong>
                    )}
                    {requestType === 'bonus' && (
                      <><br /><strong style={{ color: '#ff9800' }}>This is a one-time payment and will not affect regular salary.</strong></>
                    )}
                  </Alert>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || !inputsEnabled || !amount || !reason}
                  sx={{ 
                    textTransform: 'none', 
                    fontWeight: 'bold', 
                    borderRadius: 2, 
                    backgroundColor: config.color, 
                    py: 1.5, 
                    '&:hover': { backgroundColor: config.color, filter: 'brightness(0.9)' } 
                  }}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                >
                  {loading ? 'Submitting...' : config.buttonText}
                </Button>

                {loading && (
                  <Box sx={{ mt: 3 }}>
                    <LinearProgress sx={{ borderRadius: 2 }} />
                    <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                      Submitting request...
                    </Typography>
                  </Box>
                )}

                {requestSubmitted && (
                  <Alert severity="success" sx={{ mt: 3 }}>
                    ✅ Request submitted successfully! {requestType !== 'initial' ? 'It will be reviewed by the manager.' : ''}
                  </Alert>
                )}
              </Paper>
            </Fade>
          </Grid>

          <Grid item xs={12} md={5}>
            <Grow in timeout={600}>
              <Paper elevation={4} sx={{ p: 4, borderRadius: 4, background: 'linear-gradient(135deg, #C84646 0%, #a33a3a 100%)', color: 'white', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <CheckIcon sx={{ fontSize: 40 }} />
                  <Typography variant="h6" fontWeight="bold">Quick Guide</Typography>
                </Box>
                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', mb: 3 }} />
                
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}><strong>📝 Request Types:</strong></Typography>
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon fontSize="small" /> <strong>Initial Setup:</strong> Set first salary for new employee (overwrites 0.00)
                  </Typography>
                </Box>
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon fontSize="small" /> <strong>Increment:</strong> Permanent salary increase (requires approval)
                  </Typography>
                </Box>
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingDownIcon fontSize="small" /> <strong>Downgrade:</strong> Permanent salary reduction (requires approval)
                  </Typography>
                </Box>
                <Box sx={{ mb: 3, p: 1.5, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BonusIcon fontSize="small" /> <strong>Bonus:</strong> One-time payment (requires approval, doesn't affect regular salary)
                  </Typography>
                </Box>
                
                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', mb: 3 }} />
                
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}><strong>🔍 Search Tips:</strong></Typography>
                <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>• Type employee name (e.g., "Nicholas", "John")</Typography>
                <Typography variant="body2" sx={{ mb: 1, opacity: 0.9 }}>• Type phone number (e.g., "08065514429")</Typography>
                <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>• Select from the dropdown results</Typography>
                
                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', mb: 3 }} />
                
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}><strong>📋 How it works:</strong></Typography>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>1. Search and select employee by name or phone</Typography>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>2. Select request type (Initial/Increment/Downgrade/Bonus)</Typography>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>3. Enter the amount and reason</Typography>
                <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>4. Submit for Manager approval (except Initial Setup)</Typography>
                
                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', mb: 3 }} />
                
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  <strong>💡 Note:</strong> Initial salary setup is applied immediately. All other requests require manager approval.
                </Typography>
                
                <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center' }}>Secure • Fast • Reliable</Typography>
                </Box>
              </Paper>
            </Grow>
          </Grid>
        </Grid>

        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Typography variant="body2" color="textSecondary">Designed by Elegantè IT</Typography>
        </footer>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: config.color, color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {config.icon}
            Confirm {requestType === 'initial' ? 'Initial Salary Setup' : (requestType === 'increment' ? 'Increment' : (requestType === 'downgrade' ? 'Downgrade' : 'Bonus'))} Request
          </Box>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity={requestType === 'initial' ? 'info' : 'warning'}>
              <strong>Please review the details:</strong><br />
              Staff: {staffInfo?.full_name}<br />
              Department: {staffInfo?.department}<br />
              {requestType !== 'initial' && `Current Salary: ${formatCurrency(staffInfo?.salary_amount)}<br />`}
              {requestType === 'initial' && `Initial Salary to Set: ${formatCurrency(preview.amount)}<br />`}
              {requestType === 'increment' && `Increment Amount: ${formatCurrency(preview.amount)}<br />`}
              {requestType === 'downgrade' && `Downgrade Amount: ${formatCurrency(preview.amount)}<br />`}
              {requestType === 'bonus' && `Bonus Amount: ${formatCurrency(preview.amount)}<br />`}
              {requestType !== 'bonus' && requestType !== 'initial' && `New Salary: ${formatCurrency(preview.newValue)}<br />`}
              {requestType === 'initial' && `New Salary: ${formatCurrency(preview.newValue)}<br />`}
              Reason: {reason}
            </Alert>
            <Typography variant="body2" color="textSecondary">
              {requestType === 'initial' 
                ? "This will immediately set the employee's initial salary."
                : requestType === 'bonus' 
                  ? "This bonus request will be reviewed by management. Once approved, it will be processed as a one-time payment."
                  : "This request will be sent to the manager for approval. The change will take effect after approval."}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmSubmit} variant="contained" sx={{ bgcolor: config.color }}>
            Confirm Submission
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestSalaryAdjustment;